(() => {
  class FlavorFacts extends HTMLElement {
    connectedCallback() {
      this.setupArc();
      this.setupStack();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    /* Lays the heading letters along a shallow arc. Each letter is placed by
       arc length, so the spacing follows the real glyph widths. */
    setupArc() {
      const arc = this.querySelector('.ff-arc');
      if (!arc) return;

      const letters = Array.from(arc.querySelectorAll('.ff-arc__letter'));
      if (!letters.length) return;

      let lastWidth = 0;

      const layout = (force) => {
        const width = arc.clientWidth;
        if (!width) return;
        // Setting the height below re-triggers the observer; only redo the
        // placement when the width actually changed.
        if (!force && width === lastWidth) return;
        lastWidth = width;

        // Bigger multiplier = flatter arc.
        const radius = width * (Number(arc.dataset.curve) || 1.2);

        // offsetWidth, not getBoundingClientRect: the latter reports the
        // *transformed* box, and these letters are rotated, so re-measuring
        // would inflate the widths a little more on every pass.
        const tracking = Number(arc.dataset.tracking) || 1;
        const widths = letters.map((el) => el.offsetWidth * tracking);
        const total = widths.reduce((sum, w) => sum + w, 0);

        let cursor = -total / 2;
        letters.forEach((el, i) => {
          const centre = cursor + widths[i] / 2;
          cursor += widths[i];
          const angle = (centre / radius) * (180 / Math.PI);
          el.style.setProperty('--ff-r', `${Math.round(radius)}px`);
          el.style.setProperty('--ff-a', `${angle.toFixed(2)}deg`);
        });

        // Now that the letters are placed, shrink the box to their real extent.
        // getBoundingClientRect is correct here — it is the visual bounds we
        // want, and this value never feeds back into the width measurements.
        const top = arc.getBoundingClientRect().top;
        const bottom = letters.reduce(
          (lowest, el) => Math.max(lowest, el.getBoundingClientRect().bottom - top),
          0
        );
        arc.style.setProperty('--ff-arc-h', `${Math.ceil(bottom)}px`);

        arc.setAttribute('data-ready', '');
      };

      // Measure once fonts are ready, otherwise the widths come from the fallback.
      layout(true);
      document.fonts?.ready.then(() => layout(true));

      this.resizeObserver = new ResizeObserver(() => layout(false));
      this.resizeObserver.observe(arc);
    }

    /* Two cards stacked in depth: the front one sits forward, the other drops
       below it and further back. Choosing a tab swaps which is which. */
    setupStack() {
      const stack = this.querySelector('.ff-flip');
      if (!stack) return;

      const buttons = Array.from(this.querySelectorAll('.ff-toggle__btn'));
      // Read from the markup rather than a fixed pair: the middle card is
      // optional, so the stack is two or three deep depending on the settings.
      const order = Array.from(stack.querySelectorAll('.ff-face'));
      const faces = {};
      order.forEach((el) => {
        if (el.dataset.face) faces[el.dataset.face] = el;
      });
      if (!order.length) return;

      const SWAP_CLASSES = ['ff-face--leaving', 'ff-face--arriving'];
      const animated = this.dataset.animate !== 'false';

      const show = (face, animate) => {
        if (stack.dataset.face === face) return;
        stack.dataset.face = face;
        buttons.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.face === face)));

        // The card behind stays clickable — its exposed strip is how you bring
        // it forward — so it is hidden from assistive tech with aria-hidden
        // rather than inert. Safe here: the cards hold no focusable content.
        // Cards behind are stacked in markup order, each one step deeper, so a
        // third card peeks below the second rather than hiding under it.
        let depth = 0;
        order.forEach((el) => {
          const isFront = el.dataset.face === face;
          el.dataset.state = isFront ? 'front' : 'back';
          el.setAttribute('aria-hidden', String(!isFront));
          el.style.setProperty('--ff-depth', isFront ? 0 : ++depth);

          el.classList.remove(...SWAP_CLASSES);
          if (!animate) return;
          // Restart the animation even when swapping back mid-flight.
          void el.offsetWidth;
          el.classList.add(isFront ? 'ff-face--arriving' : 'ff-face--leaving');
        });
      };

      Object.values(faces).forEach((el) => {
        el?.addEventListener('animationend', () => el.classList.remove(...SWAP_CLASSES));
      });

      buttons.forEach((btn) => btn.addEventListener('click', () => show(btn.dataset.face, animated)));

      // Clicking the card behind brings it forward.
      Object.entries(faces).forEach(([name, el]) => {
        el?.addEventListener('click', () => {
          if (el.dataset.state === 'back') show(name, animated);
        });
      });

      show('front', false);
    }
  }

  if (!customElements.get('flavor-facts')) {
    customElements.define('flavor-facts', FlavorFacts);
  }
})();
